import PartnershipIcon from 'components/Common/Vendors/Partnership/PartnershipIcon'
import Tooltip from 'LuxKit/Tooltip'
import GeoContext from 'contexts/geoContext'
import { useContext, useMemo } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import IconLabel from './IconLabel'
import Label from './Label'

type Variant = 'default' | 'prominent' | 'icon' | 'icon-prominent'
type LabelType = 'use-only' | 'earn-and-use' | 'earn-points' | 'earn-only'
interface Config extends Omit<React.ComponentProps<typeof Label>, 'children' | 'style'> {
  style?: (partnership: App.Partnership) => React.CSSProperties
}

const variantConfig: Record<Variant, Config> = {
  default: {
    kind: 'plain',
    variant: 'dark',
    startIcon: <PartnershipIcon />,
  },
  icon: {
    kind: 'secondary',
    variant: 'ghost',
  },
  'icon-prominent': {
    kind: 'primary',
    variant: 'ghost',
    style: (partnership) => ({
      backgroundColor: partnership.color,
    }),
  },
  prominent: {
    kind: 'primary',
    variant: 'dark',
    startIcon: <PartnershipIcon />,
    style: (partnership) => ({
      backgroundColor: partnership.color,
    }),
  },
}

interface Props {
  /**
   * @default default
   */
  variant?: Variant
  /**
   * @default true (I have no idea why)
   */
  canEarn?: boolean
  /**
   * @default true (I have no idea why)
   */
  canUse?: boolean
  /**
   * The number of points the user will earn
   */
  points?: number
  className?: string
}

/**
 * _**NOTE**: Partnership label relies on the value of the `partnership` attribute in the Geo state._
 */
function PartnershipLabel(props: Props) {
  const { variant = 'default', canEarn = true, canUse = true, className, points } = props
  const { partnership } = useContext(GeoContext)
  const intl = useIntl()

  const labelType = useMemo<LabelType | undefined>(() => {
    if (partnership) {
      const hasEarn = canEarn && partnership.hasEarn
      const hasUse = canUse && partnership.hasBurn
      if (points) {
        return 'earn-points'
      } else if (hasEarn && hasUse) {
        return 'earn-and-use'
      } else if (hasEarn) {
        return 'earn-only'
      } else if (hasUse) {
        return 'use-only'
      }
    }
  }, [canEarn, canUse, partnership, points])

  const config = useMemo<Config | undefined>(() => {
    if (!partnership) return undefined
    return variantConfig[variant]
  }, [partnership, variant])

  const labelContent = useMemo(() => {
    if (!partnership || !labelType) return undefined
    const i18nLabelMap: Record<LabelType, string> = {
      'use-only': intl.formatMessage(
        {
          id: 'sseLFigTXpjlRqqv-A8L',
          defaultMessage: 'Use {rewardName}',
        },
        { rewardName: partnership.rewardName },
      ),
      'earn-and-use': intl.formatMessage(
        {
          id: 'SYC8uS8v_BaLodVy6TrP',
          defaultMessage: 'Earn & Use {rewardName}',
        },
        { rewardName: partnership.rewardName },
      ),
      'earn-points': intl.formatMessage(
        {
          id: '6ed5fXkirpZXhUOC9cPT',
          defaultMessage: 'Earn {formattedPoints} {rewardName}',
        },
        {
          formattedPoints: intl.formatNumber(Math.floor(points ?? 0)),
          rewardName: partnership.rewardName,
        },
      ),
      'earn-only': intl.formatMessage(
        {
          id: '0Zdy8VjgY_LIf1sQJ6Io',
          defaultMessage: 'Earn {rewardName}',
        },
        { rewardName: partnership.rewardName },
      ),
    }
    return i18nLabelMap[labelType]
  }, [intl, partnership, labelType, points])

  if (!config || !labelType || !partnership || !labelContent) {
    return null
  }

  if (variant === 'icon' || variant === 'icon-prominent') {
    return <Tooltip
      className={className}
      placement="end"
      description={labelContent}
    >
      <IconLabel
        {...config}
        style={config.style?.(partnership)}
        icon={<PartnershipIcon />}
      />
    </Tooltip>
  }

  return <Label
    {...config}
    style={config.style?.(partnership)}
    className={className}
    data-testid="partnership-label"
  >
    {labelContent}
  </Label>
}

export default PartnershipLabel
